import React, { useState, useEffect, useRef } from "react";
import "./FamilyTreeLoading.css";
import crestImage from "../../../assets/DuPontCrestTree.jpg";

const MESSAGES = [
  // Original quips
  "Dusting off the archives\u2026",
  "Unrolling two centuries of parchment\u2026",
  "Polishing the family silver\u2026",
  "Counting cousins \u2014 there are a lot\u2026",
  "Untangling thirteen generations\u2026",
  "Alphabetizing 7,600 relatives\u2026",
  "Lighting the library candles\u2026",
  "Deciphering old handwriting\u2026",
  "Consulting the family butler\u2026",
  "Pressing flowers between the pages\u2026",
  "Sorting through centuries of drama\u2026",
  "Waking up the genealogists\u2026",
  "Unfurling the family banners\u2026",
  "Convincing the ancestors to cooperate\u2026",
  "Retracing footsteps from Wilmington to Paris\u2026",
  // Historical
  "The family patriarch was literally saved from the guillotine by Robespierre\u2019s fall \u2014 48 hours later and there\u2019s no family tree",
  "E.I. du Pont learned gunpowder chemistry from Lavoisier (the man who discovered oxygen)",
  "They supplied gunpowder to the Lewis & Clark expedition",
  "They lived above their own gunpowder factory for 88 years \u2014 with 288 explosions",
  "A du Pont secretly helped Jefferson negotiate the Louisiana Purchase",
  "T. Coleman du Pont built the first divided highway in the world as a personal gift to Delaware",
  // Estates
  "Henry Francis grew his house to 175 rooms and filled it with 90,000 antiques",
  "Alfred I. built a 10-foot wall topped with broken glass around Nemours \u2014 kids were paid a penny per bottle",
  "Longwood Gardens has 600 fountain jets and 4.5 acres of greenhouse",
  "\u201Cdu Pont\u201D literally means \u201Cof the bridge\u201D in French",
  // Family scale
  "3,500+ living relatives today",
  "The family once employed 1 in 10 Delawareans",
];

// Shuffle so the order feels different every visit
function shuffleArray(arr) {
  const shuffled = [...arr];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
}

const MESSAGE_INTERVAL = 3000; // rotate every 3 seconds
const REVEAL_DURATION = 800; // fade-out length in ms

function FamilyTreeLoading({ isLoading, onRevealComplete }) {
  const [messageIndex, setMessageIndex] = useState(0);
  const [messageFading, setMessageFading] = useState(false);
  const [isRevealing, setIsRevealing] = useState(false);
  const [shouldRender, setShouldRender] = useState(true);
  const timerRef = useRef(null);
  const shuffledMessages = useRef(shuffleArray(MESSAGES));

  // Rotate messages while still loading
  useEffect(() => {
    if (isLoading && !isRevealing) {
      timerRef.current = setInterval(() => {
        setMessageFading(true);
        setTimeout(() => {
          setMessageIndex((prev) => (prev + 1) % shuffledMessages.current.length);
          setMessageFading(false);
        }, 500); // half-second crossfade
      }, MESSAGE_INTERVAL);
    }

    return () => clearInterval(timerRef.current);
  }, [isLoading, isRevealing]);

  // Begin reveal animation when loading finishes
  useEffect(() => {
    if (!isLoading && !isRevealing) {
      setIsRevealing(true);
      clearInterval(timerRef.current);

      const timeout = setTimeout(() => {
        setShouldRender(false);
        if (onRevealComplete) onRevealComplete();
      }, REVEAL_DURATION);

      return () => clearTimeout(timeout);
    }
  }, [isLoading, isRevealing, onRevealComplete]);

  if (!shouldRender) return null;

  return (
    <div
      className={`archive-curtain ${isRevealing ? "archive-curtain--revealing" : ""}`}
    >
      <div className="archive-curtain-inner">
        {/* Gold corner flourishes */}
        <div className="corner-flourish--tl" />
        <div className="corner-flourish--tr" />
        <div className="corner-flourish--bl" />
        <div className="corner-flourish--br" />

        {/* Du Pont crest */}
        <div style={{
          width: '160px', height: '160px', borderRadius: '50%',
          background: '#fff', display: 'flex', alignItems: 'center',
          justifyContent: 'center', marginBottom: '24px', overflow: 'hidden',
          boxShadow: '0 4px 20px rgba(0,0,0,0.3)'
        }} className="archive-crest">
          <img
            src={crestImage}
            alt="Du Pont family crest"
            style={{ width: '88%', height: '88%', objectFit: 'contain' }}
          />
        </div>

        {/* Rotating quip */}
        <div className="archive-message-container">
          <p
            className={`archive-message ${
              messageFading ? "archive-message--fading" : "archive-message--visible"
            }`}
          >
            {shuffledMessages.current[messageIndex]}
          </p>
        </div>

        {/* Pulsing dots */}
        <div className="archive-dots">
          <span className="archive-dot" />
          <span className="archive-dot" />
          <span className="archive-dot" />
        </div>
      </div>
    </div>
  );
}

export default FamilyTreeLoading;
